<script setup lang="ts">
import {onMounted, ref, watch, reactive} from "vue";
import CustomButton from "@/components/Button/Button.vue";
import TripCard from "@/components/TripCard/TripCard.vue";
import { useI18n } from 'vue-i18n'; // Import i18n
import { useTripsStore } from "@/stores/tripsStore";
import {useSearchStore} from "@/stores/searchStore";
import TripAddModal from "@/components/TripCard/TripAddModal.vue";
import {useUserStore} from "@/stores/userStore";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";


const { t } = useI18n(); // Initialize i18n

const showAddTripModal = ref(false);
const tripsStore = useTripsStore();
const isLoading = ref(true);
const isCreatingTrip = ref(false);
const userStore = useUserStore();




const handleTripCreated = async () => {
  await tripsStore.getAll();
  showAddTripModal.value = false;
};

const handleTripUpdated = async () => {
  await tripsStore.getAll();
};


watch(
    () => userStore.sessionRestored,
    async (restored) => {
      if (restored) {
        try {
          isLoading.value = true;

          if (!userStore.user) {
            await userStore.fetchUserProfile();
          }

          await tripsStore.getAll();
        } finally {
          isLoading.value = false;
        }
      }
    },
    {immediate: true}
);

</script>

<template>

  <div class="container flex flex-col mt-5">
    <div class="w-full flex flex-row justify-between items-end">
      <h1 class="trips__title responsive-title-3 font-semibold">
        {{ t('myTrips') }}
      </h1>
      <CustomButton v-if="tripsStore.hasTrips()"
                    :outlined="false"
                    :rounded="true"
                    type="black"
                    class="trips-add__button "
                    @click="showAddTripModal = true">
        {{ t('createNewTrip') }}
      </CustomButton>
    </div>
    <div :class="`trips-grid mt-5 ${!tripsStore.hasTrips() ? 'content-center min-h-[68.5vh]' : ''}`">

      <div v-if="!isLoading">
        <div v-if="!tripsStore.hasTrips()" class="flex justify-center items-center lg:gap-6 gap-4">
          <div class="flex flex-col bg-background-light box-class justify-between rounded-12xl p-7">
            <div>
              <img src="@/assets/icons/svg/Luggage_40x40.svg" alt="Suitcase">
            </div>
            <div class="responsive-body-large font-semibold">{{ t('noTrips') }}</div>
          </div>
          <div @click="showAddTripModal = true"
               class="flex flex-col bg-primary-blue text-white justify-center items-center rounded-12xl p-5 gap-4 cursor-pointer box-class"
               type="primary">
            <img src="@/assets/icons/svg/Add_44x44_white.svg" alt="Plus" width="48"/>
            <div class="responsive-body-large font-semibold">{{ t('addNewTrip') }}</div>
          </div>
        </div>
        <div v-else-if="tripsStore.hasTrips()"
             class="trip-grid-content grid 2xl:grid-cols-5 lg:grid-cols-4 grid-cols-3  gap-6">
          <template v-for="(trip, index) in tripsStore.trips" :key="trip.slug">
            <div class="relative">
              <TripCard :trip="trip" />
            </div>
          </template>
          <div v-if="isCreatingTrip" class="flex justify-center items-center border border-dashed rounded-xl min-h-[200px]">
            <CustomSpinner/>
          </div>
        </div>
      </div>
      <div v-else class="w-full flex items-center justify-center py-12 min-h-[200px]">
        <div class="w-fit flex flex-col rounded-11xl">
          <CustomSpinner/>
        </div>
      </div>
    </div>
  </div>

  <TripAddModal
      v-model:show="showAddTripModal"
      @on-trip-created="handleTripCreated"
      @on-image-updated="handleTripUpdated"
  />
</template>

<style scoped>
/*:deep(.modal-content) {
  @apply w-1/2;
}*/

.box-class {
  width: 206px;
  height: 15.5rem;
}

@media (max-width: 1600px) {
  .box-class {
    min-width: 14vw;
  }
}
@media (max-width: 1350px) {
  .box-class {
    min-width: 19vw;
  }
}



@media (max-width: 786px) and (min-width: 541px) {
  .trip-grid-content {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 540px) {
  .trips-grid {
    align-content: start;
    min-height: 20rem;
  }
  .box-class {
    min-width: 42vw;
    height: 11.4375rem;
  }
  .box-class img[alt="Plus"] {
    height: 2.75rem;
    width: 2.75rem;
  }
  .box-class img[alt="Suitcase"] {
    height: 1.875rem;
    width: 1.875rem;
  }
  .trip-grid-content {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}



</style>
